import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SHOP_SECTION_ID, scrollToSection } from '../utils/scroll';
import { goToShop } from '../utils/shopIntent';
import { pricingConfig } from '../data/pricing';
import type { BundleId } from '../data/pricing';
import { HomeHero } from '../components/home/HomeHero';
import { QuickPick } from '../components/home/QuickPick';
import { ProductReveal } from '../components/home/ProductReveal';
import { BuiltDifferent } from '../components/home/BuiltDifferent';
import { MountTeaser } from '../components/home/MountTeaser';
import { PromiseSection } from '../components/home/PromiseSection';
import { ApplicationsStrip } from '../components/home/ApplicationsStrip';
import { ClosingCta } from '../components/home/ClosingCta';
import { ShopTabs } from '../components/shop/ShopTabs';
import { TestimonialsMarquee } from '../components/home/TestimonialsMarquee';
import { Recommender } from '../components/shop/Recommender';
import { SpecSheet } from '../components/home/SpecSheet';
import { AlwaysReady } from '../components/home/AlwaysReady';
import { FeaturedSystems } from '../components/shop/FeaturedSystems';
import { PositionsCalculator } from '../components/shop/PositionsCalculator';
import { useLanguage } from '../contexts/LanguageContext';
export function Home() {
  const {
    t
  } = useLanguage();
  const {
    hash,
    search
  } = useLocation();

  // Arriving via /#shop-systems (SHOP NOW from another page), optionally with ?system=sys8.
  useEffect(() => {
    const target = hash.replace('#', '');
    if (target !== SHOP_SECTION_ID && target !== 'reviews') return;
    const system = new URLSearchParams(search).get('system');
    const intent = system && pricingConfig.bundles.some(b => b.id === system) ? system as BundleId : null;
    const id = window.setTimeout(() => intent ? goToShop(intent) : scrollToSection(target), 80);
    return () => window.clearTimeout(id);
  }, [hash, search]);
  return <>
      <HomeHero data-id="element-1008" data-mp="1kk7gyw091ega" />
      <QuickPick data-id="element-1009" data-mp="1kjnxhauyai05" />
      <ProductReveal data-id="element-1010" data-mp="1kj5s9oqb8tg3" />
      <SpecSheet data-id="element-1011" data-mp="1kmtowwr1xvz1" />
      <BuiltDifferent data-id="element-1012" data-mp="1omc8np75010a" />
      <MountTeaser data-id="element-1013" data-mp="1omkl5fzcl4ay" />
      <AlwaysReady data-id="element-1014" data-mp="1on3sk2rdgaaq" />
      <FeaturedSystems data-id="element-1015" data-mp="1onncfp0l18re" />

      <section id={SHOP_SECTION_ID} tabIndex={-1} aria-labelledby="paths-title" className="scroll-mt-[72px] border-t mf-hairline bg-deep focus:outline-none" data-id="element-1016" data-mp="1amj8l39q08fn">
        <div className="mx-auto max-w-site px-5 py-24 md:px-8 md:py-32" data-id="element-1017" data-mp="23b8ctb62otvm">
          <h2 id="paths-title" className="max-w-4xl font-display text-5xl font-semibold uppercase leading-[0.92] md:text-7xl" data-id="element-1018" data-mp="23c07mjruinku">
            Choose your <span className="text-ice" data-id="element-1019" data-mp="1tloqtwn2cjwj">MarineForceLED System</span>
          </h2>
          <p className="mt-5 font-display text-lg font-semibold uppercase tracking-[0.08em] text-white md:text-xl" data-id="element-1020" data-mp="1tjkohj4tbsyd">
            From your first lighting position to a complete backup lighting system.
          </p>
          <p className="mt-5 max-w-2xl text-lg text-titanium" data-id="element-1021" data-mp="2r0j6k8p09o09">
            M1 PRO combines rechargeable professional-grade lighting with a fixed magnetic mounting system. Your mounting
            positions stay installed; your lights come off for charging and swap with charged backup units — consistent
            placement, easier rechargeable lighting.
          </p>
          <p className="mt-4 font-display text-base font-semibold uppercase tracking-[0.14em] text-ice" data-id="element-1022" data-mp="og8d8um60uvn">Install once. Swap whenever you need.</p>
          <div className="mt-12" data-id="element-1023" data-mp="3srke9mh66f7q">
            <ShopTabs data-id="element-1024" data-mp="3ssvq89krqt9x" />
          </div>
        </div>
      </section>

      <section id="find-system" tabIndex={-1} aria-labelledby="positions-title" className="scroll-mt-[72px] border-t mf-hairline bg-[#091C31] focus:outline-none" data-id="element-1025" data-mp="3nu12ct7y2eiy">
        <div className="mx-auto max-w-site px-5 py-24 md:px-8 md:py-32" data-id="element-1026" data-mp="ni5yyp7izrp">
          <h2 id="positions-title" className="max-w-3xl font-display text-5xl font-semibold uppercase leading-[0.92] md:text-6xl" data-id="element-1027" data-mp="1h2jfe9pf73uk">
            How many lighting positions <span className="text-ice" data-id="element-1028" data-mp="1c3gmfnmpso2y">do you want?</span>
          </h2>
          <p className="mt-5 max-w-xl text-lg text-titanium" data-id="element-1029" data-mp="27f6mdnpqn399">
            Pick your positions and we’ll match the official system — with an optional charged backup set.
          </p>
          <div className="mt-12" data-id="element-1030" data-mp="2hbt4i4qojg7l">
            <PositionsCalculator data-id="element-1031" data-mp="2harhr30ax5a1" />
          </div>
        </div>
      </section>

      <section aria-labelledby="rec-title" className="border-t mf-hairline bg-deep" data-id="element-1032" data-mp="2mbxc159m10lo">
        <div className="mx-auto max-w-site px-5 py-24 md:px-8 md:py-32" data-id="element-1033" data-mp="2qdivj9gngd4u">
          <h2 id="rec-title" className="max-w-3xl font-display text-5xl font-semibold uppercase leading-[0.92] md:text-6xl" data-id="element-1034" data-mp="2vbke7ta5yafs">
            {t('rec.headline')}
          </h2>
          <div className="mt-12" data-id="element-1035" data-mp="304na482exvz5">
            <Recommender data-id="element-1036" data-mp="3526nz6v786qk" />
          </div>
        </div>
      </section>

      <TestimonialsMarquee data-id="element-1037" data-mp="353hsxa0drkj9" />
      <PromiseSection data-id="element-1038" data-mp="3a19ukvqtul0n" />
      <ApplicationsStrip data-id="element-1039" data-mp="3a1rqtsdhoz8k" />
      <ClosingCta data-id="element-1040" data-mp="3a2kxktcg7oqp" />
    </>;
}